/**
 * Role Selection Page
 * Shown after login for users with multiple roles
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { ROLE_LABELS, ROLE_STYLES } from '../hooks/usePermissions';
import { 
  Shield, 
  Users, 
  ClipboardList, 
  Stethoscope,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// Role icons and descriptions
const ROLE_INFO = {
  admin: {
    icon: Shield,
    description: 'Full system access including settings, user management, and all features',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10 hover:bg-red-500/20 border-red-500/20',
  },
  manager: {
    icon: ClipboardList,
    description: 'Supervisory access with compliance authority, document approval, and reports',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20',
  },
  team_leader: { // Backwards compatibility
    icon: ClipboardList,
    description: 'Supervisory access with compliance authority, document approval, and reports',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20',
  },
  ops_coordinator: {
    icon: Users,
    description: 'Operations access including shifts, scheduling, medics, and expenses',
    color: 'text-[#FFC04D]',
    bgColor: 'bg-[#FFC04D]/10 hover:bg-[#FFC04D]/20 border-[#FFC04D]/20',
  },
  medic: {
    icon: Stethoscope,
    description: 'Personal portal to view shifts, submit expenses, and manage documents',
    color: 'text-[#1ABC9C]',
    bgColor: 'bg-[#1ABC9C]/10 hover:bg-[#1ABC9C]/20 border-[#1ABC9C]/20',
  },
};

// Normalize role for display (handle backwards compatibility)
const normalizeRole = (role) => {
  if (role === 'team_leader') return 'manager';
  return role;
};

export default function RoleSelection() {
  const navigate = useNavigate();
  const { user, userRoles, selectRole, logout } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRoleSelect = async (role) => {
    setSelectedRole(role);
    setLoading(true);
    
    try {
      await selectRole(role);
      toast.success(`Logged in as ${ROLE_LABELS[normalizeRole(role)] || role}`);
      
      // Navigate based on role
      if (role === 'medic') {
        navigate('/portal');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Role selection error:', error);
      toast.error('Failed to select role. Please try again.');
      setSelectedRole(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get unique normalized roles for display
  const displayRoles = [...new Set(userRoles.map(normalizeRole))];

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#F2F2F2]">
            <span className="text-[#1ABC9C]">Elara</span> Systems
          </h1>
          <p className="text-[#A1A1AA] mt-2">Workforce Management</p>
        </div>

        <Card className="bg-[#2B2B2B] border-white/5" data-testid="role-selection-card">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-[#F2F2F2]">
              Welcome back, {user?.name?.split(' ')[0] || 'User'}
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              Select which role you would like to use for this session
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3 pt-4">
            {displayRoles.map((role) => {
              const roleInfo = ROLE_INFO[role] || ROLE_INFO.ops_coordinator;
              const Icon = roleInfo.icon;
              const isSelected = selectedRole === role;
              
              return (
                <button
                  key={role}
                  onClick={() => handleRoleSelect(role)}
                  disabled={loading}
                  className={`
                    w-full p-4 rounded-lg border transition-all duration-200
                    flex items-center gap-4 text-left
                    ${roleInfo.bgColor}
                    ${isSelected ? 'ring-2 ring-[#1ABC9C]' : ''}
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                  data-testid={`role-option-${role}`}
                >
                  <div className={`p-3 rounded-lg bg-[#1A1A1A] ${roleInfo.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold ${roleInfo.color}`}>
                      {ROLE_LABELS[role] || role}
                    </h3>
                    <p className="text-sm text-[#A1A1AA] line-clamp-2">
                      {roleInfo.description}
                    </p>
                  </div>
                  
                  {loading && isSelected ? (
                    <Loader2 className="w-5 h-5 text-[#1ABC9C] animate-spin" />
                  ) : (
                    <ChevronRight className={`w-5 h-5 ${roleInfo.color}`} />
                  )}
                </button>
              );
            })}

            <div className="pt-4 border-t border-white/5">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full text-[#A1A1AA] hover:text-[#F2F2F2] hover:bg-white/5"
                data-testid="logout-from-selection"
              >
                Sign out and use a different account
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[#A1A1AA] mt-6">
          Your role selection is valid for this browser session only.
          <br />
          You will need to select again after closing the browser.
        </p>
      </div>
    </div>
  );
}
